import { h, Host } from "@stencil/core";
import Prism from "prismjs";
import { debounce } from "../../utils/debounce";
import copyToClipboard from "../../utils/copyToClipboard";
import { logger } from "../../utils/console";
/** @static base class name */
const baseClass = "ux-code";
export class Code {
    constructor() {
        this.code = "";
        this.copyButton = "Copy";
        this.language = "none";
        this.lineNumbers = false;
        this.inline = false;
        this.theme = "light";
        this.formattedCode = undefined;
        this.isCopied = undefined;
        this.numLines = 0;
    }
    codeElement;
    updateCode() {
        this.formatCode();
    }
    componentWillLoad() {
        this.updateCode();
    }
    formatCode = debounce(async () => {
        const config = {
            code: this.code.trim(),
            language: this.language === undefined ? "none" : this.language.toLowerCase(),
            grammar: this.language === undefined ? "none" : this.language.toLowerCase()
        };
        try {
            switch (config.language) {
                case "css":
                case "scss":
                case "sass":
                    config.grammar = Prism.languages.css;
                    break;
                case "js":
                case "javascript":
                case "typescript":
                case "react":
                case "jsx":
                case "tsx":
                case "php":
                    config.grammar = Prism.languages.javascript;
                    break;
                case "html":
                case "markup":
                case "xml":
                default:
                    config.grammar = Prism.languages.html;
                    break;
            }
            this.formattedCode = Prism.highlight(config.code, config.grammar, config.language);
            this.codeElement.innerHTML = this.formattedCode;
            this.numLines = this.formattedCode.split("\n").length;
        }
        catch (err) {
            this.codeElement.innerHTML = config.code;
            this.numLines = config.code.split("\n").length;
            logger.error(`Failed to format code:`, err);
        }
    }, 100);
    copySource = async () => {
        await copyToClipboard(this.code);
        this.isCopied = true;
        window.setTimeout(() => {
            this.isCopied = false;
        }, 2000);
    };
    renderLineNumbers = () => {
        const rows = Array.from(Array(this.numLines).keys());
        return (h("div", { class: `${baseClass}__rows` }, rows.map((row) => (h("span", { key: row }, row + 1)))));
    };
    render() {
        const { language, theme, copyButton, copySource, isCopied, lineNumbers, numLines, inline } = this;
        return (h(Host, { key: 'e3fee5166f9a7c5a9d8418c553be51de2812d45a', class: {
                [baseClass]: true,
                [`${baseClass}--inline`]: inline,
                [`${baseClass}--line-numbers`]: lineNumbers,
                [`${baseClass}--theme-${theme}`]: ["light", "dark"].includes(theme)
            } }, h("pre", { key: 'a380761067cb3e1e8235b98b4ffe4ebcef614117', class: `${baseClass}__container language-${language}` }, lineNumbers && numLines > 0 && this.renderLineNumbers(), h("code", { key: '30965fa247a72c0c638c1cb1ee14a574f2392d2f', class: `language-${language}`, ref: (el) => (this.codeElement = el) })), !inline && (h("div", { key: '13aeab74196dd7e5c37af3a3706ae8c4fe7690c0', class: `${baseClass}__copy` }, h("button", { key: '4cfc56c61429e764731a3c86b4abe2a5f67f935a', class: `${baseClass}__copy-btn`, type: "button", onClick: copySource }, copyButton, !isCopied && h("ux-icon", { key: 'c8b4b321bf5a920b35ebe58fdedc3387f38fdf7d', name: "copy", weight: "normal" }), isCopied && h("ux-icon", { key: 'aa2bcb762bbe1c7181aec8036112c7eb00ded7b5', name: "checkmark", weight: "normal" }))))));
    }
    static get is() { return "ux-code"; }
    static get originalStyleUrls() {
        return {
            "$": ["code.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["code.css"]
        };
    }
    static get properties() {
        return {
            "code": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The source code to display"
                },
                "attribute": "code",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "copyButton": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Label for the copy-to-clipboard button"
                },
                "attribute": "copy-button",
                "reflect": false,
                "defaultValue": "\"Copy\""
            },
            "language": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The language used for syntax highlighting"
                },
                "attribute": "language",
                "reflect": false,
                "defaultValue": "\"none\""
            },
            "lineNumbers": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Flag that can be set to display line numbers"
                },
                "attribute": "line-numbers",
                "reflect": false,
                "defaultValue": "false"
            },
            "inline": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "display the code snippet inline instead of as a block"
                },
                "attribute": "inline",
                "reflect": false,
                "defaultValue": "false"
            },
            "theme": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"light\" | \"dark\"",
                    "resolved": "\"dark\" | \"light\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Theme that should be used"
                },
                "attribute": "theme",
                "reflect": false,
                "defaultValue": "\"light\""
            }
        };
    }
    static get states() {
        return {
            "formattedCode": {},
            "isCopied": {},
            "numLines": {}
        };
    }
    static get watchers() {
        return [{
                "propName": "language",
                "methodName": "updateCode"
            }, {
                "propName": "code",
                "methodName": "updateCode"
            }];
    }
}
//# sourceMappingURL=code.js.map
